import { useMemo } from 'react';
import { User, UserType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { useAllCompanies } from 'queries/account';
import { AccountFeature, useAccountFeature } from './use-account-feature';
import { PrereleaseFeature, useFeature } from './use-feature';
import { useUser } from './use-user';

export const useIsRestrictedPartnerUser = (): boolean => {
  const {
    user: {
      userType,
      company: { companyId: currentUserCompanyId },
    },
  } = useUser();
  const { data: partnerCompanies } = useAllCompanies();

  return useMemo(
    () =>
      userType === UserType.Partner &&
      (partnerCompanies ?? []).find(
        ({ companyId }) => companyId === currentUserCompanyId,
      )?.accessLevel?.partnerPermissionSetName ===
        PartnerPermissionName.Restricted,
    [currentUserCompanyId, partnerCompanies, userType],
  );
};

export const usePartnerCompanyPermission = (): {
  canPartnerTrackTime: boolean;
} => {
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const isPartnerTimeTrackingEnabled = useFeature(
    PrereleaseFeature.PartnerTimeTracking,
  );
  const { user, company } = useUser();

  return useMemo(
    () => ({
      canPartnerTrackTime:
        (isPartnerTimeTrackingEnabled &&
          isTimesheetFeatureEnabled &&
          user.userType === UserType.Partner &&
          company.canTrackTime) ??
        false,
    }),
    [
      company.canTrackTime,
      isPartnerTimeTrackingEnabled,
      isTimesheetFeatureEnabled,
      user.userType,
    ],
  );
};

export const isPartnerUser = ({
  userType,
}: Pick<User, 'userType'>): boolean => {
  return userType === UserType.Partner;
};

export const useIsCurrentUserPartner = (): boolean => {
  const { user } = useUser();

  return useMemo(() => isPartnerUser(user), [user]);
};

export const useDoesPartnerMatchPermissionName = (
  partnerPermissionName: PartnerPermissionName,
) => {
  const {
    user: {
      userType,
      company: { companyId: currentUserCompanyId },
    },
  } = useUser();
  const { data: partnerCompanies } = useAllCompanies();

  return useMemo(
    () =>
      userType === UserType.Partner &&
      (partnerCompanies ?? []).find(
        ({ companyId }) => companyId === currentUserCompanyId,
      )?.accessLevel?.partnerPermissionSetName === partnerPermissionName,
    [currentUserCompanyId, partnerCompanies, partnerPermissionName, userType],
  );
};
